import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';

/**
 * The Missed Call Follow-up agent's screens. A caller's number arrives with
 * its middle hidden in lists, and in full only when a call is opened. Times
 * are UTC ISO strings.
 */

export type McStatus =
  | 'ringing' | 'answered' | 'message_held' | 'message_sent' | 'replied'
  | 'callback_booked' | 'needs_person' | 'stopped';

export interface McCaseCard {
  id: string;
  caller: string;
  caller_name: string | null;
  called_at: string;
  status: McStatus;
  language: string | null;
  /** The time they chose, in words: confirmed once booked, waiting for their yes before. */
  window: string | null;
  window_start: string | null;
  window_end: string | null;
  agent_name: string | null;
  needs_person_reason: string | null;
  last_message_at: string | null;
  completed_at: string | null;
  in_agencyzoom: boolean;
}

export interface McMessage {
  direction: 'in' | 'out';
  at: string;
  text: string;
  /** How far one of ours got: queued, sent, delivered, read, failed. "received" for theirs. */
  delivery: string | null;
}

export interface McCaseDetail extends McCaseCard {
  caller_full: string;
  called_number: string;
  first_message: string | null;
  agencyzoom_url: string | null;
  steps: { kind: string; at: string; title: string; detail: string | null }[];
  messages: McMessage[];
}

export interface McDay { day: string; value: number }

export interface McSpotlight {
  flow: { agency_number: string | null; team_phone: string | null; ring_seconds: number; whatsapp_from: string };
  totals: {
    calls: number; taken_by_team: number; missed: number; replied: number; booked: number;
    needs_person: number; stopped: number; waiting: number; median_minutes_to_book: number | null;
  };
  missed_by_day: McDay[];
  booked_by_day: McDay[];
  latest: McCaseCard[];
}

export type SendMode = 'auto' | 'approve';

export interface McTeamMember {
  id: number;
  name: string;
  email: string | null;
  takes_callbacks: boolean;
  languages: string[];
}

export interface McSettings {
  agency_name: string;
  assistant_name: string;
  team_phone: string | null;
  ring_seconds: number;
  call_message: string;
  first_message: string;
  send_mode: SendMode;
  time_zone: string;
}

export interface McConfiguration {
  settings: McSettings;
  /** Null until the workspace's number is set: no default, so two workspaces never share one. */
  agency_number: string | null;
  whatsapp_from: string;
  call_message_spoken: string;
  first_message_sent: string;
  team: McTeamMember[];
  team_error: string | null;
  rules: { when: string; then: string; detail: string }[];
  guardrails: { title: string; detail: string }[];
  /** Calls in the demo history; reset from the foot of the page. */
  demo_calls: number;
}

export interface McIntegration {
  id: 'phone' | 'whatsapp' | 'agencyzoom';
  name: string;
  role: string;
  detail: string;
  connected: boolean;
  error: string | null;
  activity: string;
}

export interface McSettingsChange extends Partial<Omit<McSettings, 'time_zone'>> {
  agents?: { id: number; name: string; languages: string[] }[];
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) };
}

function prefix(): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return `${tenantApiPrefix(tid)}/missed-call`;
}

async function call<T>(path: string, init: RequestInit, fallback: string): Promise<T> {
  const response = await fetch(buildApiUrl(`${prefix()}${path}`), { ...init, headers: getAuthHeaders() });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: unknown }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : fallback);
  }
  return response.json();
}

/** `live`: missed calls waiting on the client. `done`: every finished call, for the Calls section. */
export const fetchMcCases = (scope: 'live' | 'done') =>
  call<{ cases: McCaseCard[] }>(`/cases?scope=${scope}`, { method: 'GET' },
    scope === 'live' ? 'Could not load missed calls' : 'Could not load calls');
export const fetchMcCase = (id: string) =>
  call<McCaseDetail>(`/cases/${id}`, { method: 'GET' }, 'Could not load that call');
export const sendFirstMessage = (id: string) =>
  call<McCaseDetail>(`/cases/${id}/send`, { method: 'POST' }, 'Could not send the message');
export const fetchMcSpotlight = () => call<McSpotlight>('/spotlight', { method: 'GET' }, 'Could not load Spotlight');
export const fetchMcConfiguration = () =>
  call<McConfiguration>('/configuration', { method: 'GET' }, 'Could not load how the agent is set up');
export const saveMcSettings = (change: McSettingsChange) =>
  call<McConfiguration>('/settings', { method: 'PATCH', body: JSON.stringify(change) }, 'Could not save the settings');
/** Rebuild the two weeks of demo call history, up to now. Live calls are not touched. */
export const resetMcDemo = () =>
  call<McConfiguration>('/demo/reset', { method: 'POST' }, 'Could not reset the demo data');
export const fetchMcIntegrations = () =>
  call<{ integrations: McIntegration[] }>('/integrations', { method: 'GET' }, 'Could not load integrations');

/** Listen for "the calls changed" until `signal` aborts. Carries no data. */
export async function watchMcCases(onChange: () => void, signal: AbortSignal, onOpen?: () => void): Promise<void> {
  const response = await fetch(buildApiUrl(`${prefix()}/cases/stream`), {
    headers: { ...getAuthHeaders(), Accept: 'text/event-stream' }, signal,
  });
  if (!response.ok || !response.body) throw new Error(`Live updates unavailable (${response.status})`);
  onOpen?.();
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    let end = buffer.indexOf('\n\n');
    while (end !== -1) {
      if (buffer.slice(0, end).split('\n').includes('event: changed')) onChange();
      buffer = buffer.slice(end + 2);
      end = buffer.indexOf('\n\n');
    }
  }
}
